import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
  Divider,
  InputAdornment,
  Checkbox,
  CircularProgress,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import { toast } from 'react-toastify';
import { Delete as DeleteIcon, Edit as EditIcon, FileText as TipoDocumentoIcon, Plus as PlusIcon, RefreshCw, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';

function valueOrEmpty(value) {
  return value == null ? '' : String(value);
}

function safeJsonStringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_) {
    return '';
  }
}

function parseJsonOrNull(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return undefined;
  }
}

export default function AderTipiDocumento() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = useMemo(() => String(user?.role_name || user?.role || '').toLowerCase() === 'admin', [user]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('ordine_visualizzazione');

  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState('create');
  const [editId, setEditId] = useState(null);
  const [codice, setCodice] = useState('');
  const [etichetta, setEtichetta] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [termini, setTermini] = useState('');
  const [azioniJsonText, setAzioniJsonText] = useState('');
  const [urgenza, setUrgenza] = useState('');
  const [attivo, setAttivo] = useState(true);
  const [ordine, setOrdine] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const resetForm = useCallback(() => {
    setEditId(null);
    setCodice('');
    setEtichetta('');
    setDescrizione('');
    setTermini('');
    setAzioniJsonText('');
    setUrgenza('');
    setAttivo(true);
    setOrdine('');
  }, []);

  const openCreate = useCallback(() => {
    setEditMode('create');
    resetForm();
    setEditOpen(true);
  }, [resetForm]);

  const openEdit = useCallback(
    (row) => {
      setEditMode('edit');
      setEditId(Number(row.id));
      setCodice(valueOrEmpty(row.codice));
      setEtichetta(valueOrEmpty(row.etichetta));
      setDescrizione(valueOrEmpty(row.descrizione));
      setTermini(row.termini_legali_giorni == null ? '' : String(row.termini_legali_giorni));
      setAzioniJsonText(row.azioni_suggerite_default != null ? safeJsonStringify(row.azioni_suggerite_default) : '');
      setUrgenza(valueOrEmpty(row.livello_urgenza_default));
      setAttivo(Boolean(Number(row.attivo || 0)));
      setOrdine(row.ordine_visualizzazione == null ? '' : String(row.ordine_visualizzazione));
      setEditOpen(true);
    },
    []
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/ader/tipi-documento');
      setRows(Array.isArray(res?.data?.rows) ? res.data.rows : []);
    } catch (error) {
      setError('Errore caricando tipi documento');
      toast.error('Errore caricando tipi documento');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleSave = useCallback(async () => {
    if (!isAdmin) return;
    const c = String(codice || '').trim();
    const e = String(etichetta || '').trim();
    if (!c || !e) {
      toast.error('Codice ed etichetta sono obbligatori');
      return;
    }

    const parsedJson = parseJsonOrNull(azioniJsonText);
    if (parsedJson === undefined) {
      toast.error('JSON azioni suggerite non valido');
      return;
    }

    const payload = {
      codice: c,
      etichetta: e,
      descrizione: String(descrizione || '').trim() ? String(descrizione) : null,
      termini_legali_giorni: String(termini || '').trim() ? Number(termini) : null,
      azioni_suggerite_default: parsedJson,
      livello_urgenza_default: String(urgenza || '').trim() ? String(urgenza) : null,
      attivo: attivo ? 1 : 0,
      ordine_visualizzazione: String(ordine || '').trim() ? Number(ordine) : null
    };

    setLoading(true);
    try {
      if (editMode === 'create') {
        await api.post('/ader/tipi-documento', payload);
        toast.success('Tipo documento creato');
      } else {
        await api.put(`/ader/tipi-documento/${editId}`, payload);
        toast.success('Tipo documento aggiornato');
      }
      setEditOpen(false);
      resetForm();
      await fetchRows();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Errore salvando';
      toast.error(String(message));
    } finally {
      setLoading(false);
    }
  }, [azioniJsonText, attivo, codice, descrizione, editId, editMode, etichetta, fetchRows, isAdmin, ordine, resetForm, termini, urgenza]);

  const openDeleteDialog = useCallback((row) => {
    if (!isAdmin) return;
    setRowToDelete(row || null);
    setDeleteDialogOpen(true);
  }, [isAdmin]);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isAdmin) return;
    const id = Number(rowToDelete?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/ader/tipi-documento/${id}`);
      toast.success('Tipo documento eliminato');
      closeDeleteDialog();
      await fetchRows();
    } catch (error) {
      toast.error('Errore eliminando il tipo documento');
    } finally {
      setDeleteLoading(false);
    }
  }, [closeDeleteDialog, fetchRows, isAdmin, rowToDelete?.id]);

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const handleChangePage = useCallback((_event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const filteredRows = useMemo(() => {
    const term = String(filterText || '').trim().toLowerCase();
    const base = Array.isArray(rows) ? rows : [];
    const filtered = term
      ? base.filter((r) => {
          return (
            String(r.codice || '').toLowerCase().includes(term) ||
            String(r.etichetta || '').toLowerCase().includes(term) ||
            String(r.descrizione || '').toLowerCase().includes(term)
          );
        })
      : base;

    const sorted = [...filtered].sort((a, b) => {
      const isAsc = order === 'asc';
      const valA = a[orderBy];
      const valB = b[orderBy];

      if (
        orderBy === 'attivo' ||
        orderBy === 'id' ||
        orderBy === 'ordine_visualizzazione' ||
        orderBy === 'termini_legali_giorni' ||
        orderBy === 'documenti_totale' ||
        orderBy === 'documenti_percentuale'
      ) {
        const numA = Number(valA || 0);
        const numB = Number(valB || 0);
        return isAsc ? numA - numB : numB - numA;
      }

      const strA = String(valA || '').toLowerCase();
      const strB = String(valB || '').toLowerCase();
      if (strA < strB) return isAsc ? -1 : 1;
      if (strA > strB) return isAsc ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filterText, order, orderBy, rows]);

  return (
    <Box className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <TipoDocumentoIcon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Tipi documento ADER
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configurazione dei tipi documento ADER: etichetta, ordine, default, urgenza e azioni suggerite.
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRows} title="Ricarica" disabled={loading}>
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          </Button>
          {isAdmin ? (
            <Button onClick={openCreate} disabled={loading}>
              <PlusIcon className="mr-2 h-4 w-4" />
              Nuovo tipo documento
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate('/ader/coda-validazione')}>
            Indietro
          </Button>
        </div>
      </div>

      {error ? (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <Card className="shadow-sm border-none overflow-hidden rounded-xl">
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setPage(0);
            }}
            placeholder="Cerca per codice, etichetta o descrizione..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 520 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'codice'}
                    direction={orderBy === 'codice' ? order : 'asc'}
                    onClick={() => handleRequestSort('codice')}
                  >
                    Codice
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'etichetta'}
                    direction={orderBy === 'etichetta' ? order : 'asc'}
                    onClick={() => handleRequestSort('etichetta')}
                  >
                    Etichetta
                  </TableSortLabel>
                </TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'ordine_visualizzazione'}
                    direction={orderBy === 'ordine_visualizzazione' ? order : 'asc'}
                    onClick={() => handleRequestSort('ordine_visualizzazione')}
                  >
                    Ordine
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'attivo'}
                    direction={orderBy === 'attivo' ? order : 'asc'}
                    onClick={() => handleRequestSort('attivo')}
                  >
                    Attivo
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'termini_legali_giorni'}
                    direction={orderBy === 'termini_legali_giorni' ? order : 'asc'}
                    onClick={() => handleRequestSort('termini_legali_giorni')}
                  >
                    Termini (gg)
                  </TableSortLabel>
                </TableCell>
                <TableCell>Urgenza default</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'documenti_totale'}
                    direction={orderBy === 'documenti_totale' ? order : 'asc'}
                    onClick={() => handleRequestSort('documenti_totale')}
                  >
                    Totale
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'documenti_percentuale'}
                    direction={orderBy === 'documenti_percentuale' ? order : 'asc'}
                    onClick={() => handleRequestSort('documenti_percentuale')}
                  >
                    % sul totale
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" className="py-8">
                    <RefreshCw className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" className="py-8 text-gray-500">
                    Nessun tipo documento trovato
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell className="font-medium">{r.codice || '-'}</TableCell>
                    <TableCell>{r.etichetta || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {r.descrizione ? String(r.descrizione) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.ordine_visualizzazione ?? '-'}</TableCell>
                    <TableCell>
                      <Checkbox checked={Boolean(Number(r.attivo || 0))} disabled />
                    </TableCell>
                    <TableCell align="right">{r.termini_legali_giorni ?? '-'}</TableCell>
                    <TableCell>{r.livello_urgenza_default ?? '-'}</TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          border: '1px solid rgba(245, 158, 11, 0.35)',
                          backgroundColor: 'rgba(245, 158, 11, 0.04)',
                          minWidth: 56
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: 0.2 }}>
                          {Number(r.documenti_totale) || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const rawPercent = Number(r.documenti_percentuale);
                        const safePercent = Number.isFinite(rawPercent) ? rawPercent : 0;
                        const clampedPercent = Math.max(0, Math.min(100, safePercent));
                        return (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={36}
                                thickness={6}
                                sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={clampedPercent}
                                size={36}
                                thickness={6}
                                sx={{ position: 'absolute', left: 0 }}
                                color="warning"
                              />
                              <Box
                                sx={{
                                  top: 0,
                                  left: 0,
                                  bottom: 0,
                                  right: 0,
                                  position: 'absolute',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                                  {safePercent.toFixed(0)}%
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={isAdmin ? 'Modifica' : 'Solo admin'}>
                          <span>
                            <IconButton size="small" onClick={() => openEdit(r)} color="primary" disabled={!isAdmin || loading}>
                              <EditIcon size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isAdmin ? 'Elimina' : 'Solo admin'}>
                          <span>
                            <IconButton size="small" onClick={() => openDeleteDialog(r)} color="error" disabled={!isAdmin || loading}>
                              <DeleteIcon size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode === 'create' ? 'Nuovo tipo documento' : 'Modifica tipo documento'}</DialogTitle>
        <DialogContent dividers className="space-y-4">
          {!isAdmin ? (
            <Typography variant="body2" color="text.secondary">
              Permessi insufficienti.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                <TextField label="Codice" size="small" value={codice} onChange={(e) => setCodice(e.target.value)} />
                <TextField label="Etichetta" size="small" value={etichetta} onChange={(e) => setEtichetta(e.target.value)} />
              </Box>
              <TextField
                label="Descrizione"
                size="small"
                multiline
                minRows={2}
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
              />

              <Divider />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                <TextField
                  label="Attivo"
                  size="small"
                  select
                  value={attivo ? '1' : '0'}
                  onChange={(e) => setAttivo(String(e.target.value) === '1')}
                >
                  <MenuItem value="1">Sì</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </TextField>
                <TextField
                  label="Ordine visualizzazione"
                  size="small"
                  value={ordine}
                  onChange={(e) => setOrdine(e.target.value)}
                  inputProps={{ inputMode: 'numeric' }}
                />
                <TextField
                  label="Termini legali (giorni)"
                  size="small"
                  value={termini}
                  onChange={(e) => setTermini(e.target.value)}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Box>

              <TextField
                label="Urgenza default"
                size="small"
                select
                value={urgenza}
                onChange={(e) => setUrgenza(e.target.value)}
              >
                <MenuItem value="">(nessuna)</MenuItem>
                <MenuItem value="bassa">bassa</MenuItem>
                <MenuItem value="media">media</MenuItem>
                <MenuItem value="alta">alta</MenuItem>
                <MenuItem value="critica">critica</MenuItem>
              </TextField>

              <TextField
                label="Azioni suggerite default (JSON)"
                size="small"
                multiline
                minRows={6}
                value={azioniJsonText}
                onChange={(e) => setAzioniJsonText(e.target.value)}
                placeholder='Esempio: ["rateizzazione","istanza_sgravio"]'
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={() => setEditOpen(false)} disabled={loading}>
            Annulla
          </Button>
          {isAdmin ? (
            <Button onClick={handleSave} disabled={loading}>
              Salva
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Elimina tipo documento</DialogTitle>
        <DialogContent dividers className="space-y-3">
          <Typography variant="body2" color="text.secondary">
            Eliminare <strong>{String(rowToDelete?.etichetta || rowToDelete?.codice || '')}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Questa operazione rimuove il record di configurazione. I documenti già elaborati non vengono modificati.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
